/**
 * FixtureCommand.js
 * Light Fixture + Feeder ekleme/silme komutları
 *
 * Light Fixture eklenirken:
 * 1. Component (light fixture) oluşturulur
 * 2. Feeder (wire/cable) oluşturulur
 * 3. Three.js sahnesine light fixture ve wire eklenir
 *
 * ⚠️ NOT: FixtureCommand wire ekleme devre dışı - useFixture.js zaten curved wire ekliyor
 */

import BaseWiringCommand from './BaseWiringCommand';
import * as THREE from 'three';
import { createLightFixture } from '../../../factory/symbols/createLightFixtureSymbols';

class FixtureCommand extends BaseWiringCommand {
  /**
   * @param {string} action - 'ADD' veya 'DELETE'
   * @param {object} fixtureData - Light fixture component verisi
   * @param {object} feederData - Feeder verisi
   * @param {object} sceneData - Three.js scene verisi (fixture position, wire vertices, etc.)
   */
  constructor(action, fixtureData, feederData, sceneData) {
    super(
      action === 'ADD' ? 'ADD_LIGHT_FIXTURE' : 'DELETE_LIGHT_FIXTURE',
      action,
      fixtureData,
      feederData,
      sceneData,
      {
        componentType: 'LightFixture',
        cacheMethodSuffix: 'Fixture',
        wireColor: 0xffff00, // Fixture wire'ı sarı
        createWire: false // ❌ Wire ekleme devre dışı - useFixture.js curved wire ekliyor
      }
    );
    // ✅ restore metadata (used to re-enter draw mode after undo)
    this.restoreVertices = (sceneData && (sceneData.restoreVertices || sceneData.wireVertices)) || [];
    this.restoreSource = (sceneData && sceneData.restoreSource) || null;
    this.restoreCommandType = (sceneData && sceneData.restoreCommandType) || null;
    // keep any provided restoreCallback (may be passed via options or sceneData)
    this.restoreCallback = (this.options && this.options.restoreCallback) || (sceneData && sceneData.restoreCallback) || null;
  }

  /**
   * Light fixture sembolünü oluştur
   * @override
   */
  createSymbol(scene, fixtureData, sceneData) {
    // ✅ Null safety: fixturePosition kontrolü
    if (!sceneData || !sceneData.fixturePosition || sceneData.fixturePosition.length < 2) {
      console.warn('[FixtureCommand] createSymbol: fixturePosition invalid, using default (0,0,0)');
      return null; // veya default bir fixture döndür
    }

    const fixturePosition = new THREE.Vector3(
      sceneData.fixturePosition[0] || 0,
      sceneData.fixturePosition[1] || 0,
      sceneData.fixturePosition[2] || 0
    );

    const color = sceneData.color || 0xffff00;
    const fixtureType = sceneData.fixtureType || 'normal';
    const direction = sceneData.direction || 1;
    const angle = sceneData.angle || 0;

    const fixtureGroup = createLightFixture(
      scene,
      fixturePosition,
      direction,       // dir parameter
      color,
      null,
      false,          // isPreview
      fixtureType,    // symbol
      null,           // tempLineUUID
      angle,          // rotateValue
      fixtureData?.id || null  // fixtureId with null safety
    );

    return fixtureGroup;
  }

  /**
   * ✅ Undo operation with restore callback support
   * @override
   */
  undo(scene, dispatch, skipRedux = false) {
    

    // ✅ Local undo (skipRedux=true): Single fixture during drawing - use cache
    if (skipRedux) {
      
      super.undo(scene, dispatch, skipRedux);
      return;
    }

    // ✅ Global undo (skipRedux=false): Full wire + fixtures after Enter
    // Fixture wire sistemi için özel temizleme
    // sceneData'dan wire ve fixture ID'lerini al
    const wireUUID = this.sceneData?.wireUUID;
    const fixtureIds = this.sceneData?.fixtureIds || [];
    const wireId = this.sceneData?.wireId;

    

    try {
      // 1. Wire line'ı temizle (UUID ile) - curved wire group
      if (wireUUID) {
        const wireGroup = scene.getObjectByProperty('uuid', wireUUID);
        if (wireGroup) {
          
          wireGroup.traverse(child => {
            if (child.geometry) child.geometry.dispose();
            if (child.material) child.material.dispose();
          });
          scene.remove(wireGroup);
        }
      }

      // 2. Wire ID ile DE sil — HER ZAMAN çalışmalı (eskiden !wireUUID ile gate'liydi).
      // Mesh-FP sweep'i fixture wire LINE'ını (isWireLike) wireGroup'tan çıkarıp fpMesh'e
      // taşıyor → wireGroup boş kalıyor, wireUUID ile silince fpMesh'teki line LEFTOVER
      // kalıyordu. id/wireID ile traverse (parent-bağımsız) bunu yakalar.
      if (wireId) {
        const wireToRemove = [];
        scene.traverse(obj => {
          if (obj.userData?.id === wireId || obj.userData?.wireID === wireId) {
            wireToRemove.push(obj);
          }
        });
        wireToRemove.forEach(obj => {
          obj.traverse?.(child => {
            if (child.geometry) child.geometry.dispose();
            if (child.material) {
              if (Array.isArray(child.material)) child.material.forEach(m => m?.dispose?.());
              else child.material.dispose();
            }
          });
          if (obj.parent) obj.parent.remove(obj);
        });
      }

      // 3. Tüm fixture'ları temizle
      const fixturesToRemove = [];
      fixtureIds.forEach(fixtureId => {
        scene.traverse(obj => {
          if (obj.userData?.id === fixtureId) {
            fixturesToRemove.push(obj);
          }
        });
      });

      fixturesToRemove.forEach(obj => {
        
        obj.traverse(child => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) child.material.dispose();
        });
        if (obj.parent) obj.parent.remove(obj);
      });

      // 4. Preview wire'ları temizle (local undo stack'ten kalanlar)
      const previewWiresToRemove = [];
      scene.traverse(obj => {
        // Preview wire'lar name === 'tempLightFixtureWirePreview'
        if (obj.name === 'tempLightFixtureWirePreview' && obj.type === 'Line') {
          previewWiresToRemove.push(obj);
        }
      });

      previewWiresToRemove.forEach(obj => {
        
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) obj.material.dispose();
        if (obj.parent) obj.parent.remove(obj);
      });

      // 5. Wire marker'ları temizle (fixture wire marker'lar)
      const markersToRemove = [];
      scene.traverse(obj => {
        if (obj.userData?.role === 'wireMarker' && obj.userData?.parentWireId === wireId) {
          markersToRemove.push(obj);
        }
      });

      markersToRemove.forEach(obj => {
        
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) obj.material.dispose();
        if (obj.parent) obj.parent.remove(obj);
      });

    } catch (cleanupErr) {
      console.error('[FixtureCommand] Sahne temizleme hatası:', cleanupErr);
    }

    // Defensive cleanup: legacy scene objects
    try {
      const feederId = this.data.after?.feeder?.id || this.data.before?.feeder?.id;
      const compId = this.data.after?.component?.id || this.data.before?.component?.id;

      try {
        const sceneObjectCache = require('../SceneObjectCache').default;
        if (feederId) {
          const wire = sceneObjectCache.getFeeder(feederId) || sceneObjectCache.getWire?.(feederId);
          if (wire && wire.parent) {
            wire.parent.remove(wire);
            if (wire.geometry) wire.geometry.dispose();
            if (wire.material) wire.material.dispose();
          }
          sceneObjectCache.removeFeeder(feederId);
          sceneObjectCache.removeWire?.(feederId);
        }

        if (compId) {
          const comp = sceneObjectCache.getFixture?.(compId);
          if (comp && comp.parent) {
            comp.parent.remove(comp);
            comp.traverse(child => {
              if (child.geometry) child.geometry.dispose();
              if (child.material) child.material.dispose();
            });
          }
          sceneObjectCache.removeFixture?.(compId);
        }
      } catch (cacheErr) {
        // Fallback: traverse scene
        if (feederId) {
          scene.traverse((obj) => {
            if (obj.userData?.id === feederId || obj.userData?.feederId === feederId || obj.userData?.lineId === feederId) {
              try {
                if (obj.geometry) obj.geometry.dispose();
                if (obj.material) obj.material.dispose();
                if (obj.parent) obj.parent.remove(obj);
              } catch (err) {
                console.error('[FixtureCommand] error removing legacy feeder', err);
              }
            }
          });
        }

        if (compId) {
          scene.traverse((obj) => {
            if (obj.userData?.id === compId || obj.userData?.type === 'LightFixture') {
              try {
                obj.traverse(child => {
                  if (child.geometry) child.geometry.dispose();
                  if (child.material) child.material.dispose();
                });
                if (obj.parent) obj.parent.remove(obj);
              } catch (err) {
                console.error('[FixtureCommand] error removing legacy component', err);
              }
            }
          });
        }
      }
    } catch (cleanupErr) {
      console.error('[FixtureCommand] defensive cleanup failed', cleanupErr);
    }

    // Remove Redux entries
    if (!skipRedux) {
      try {
        const { removeFeeder, removeComponent } = require('../../../redux/simpleSupplySlice');
        const { deleteWire } = require('../../../redux/wireSlice');
        const { deleteTesisat } = require('../../../redux/tesisatSlice');

        const feederId = this.data.after?.feeder?.id || this.data.before?.feeder?.id;
        const compId = this.data.after?.component?.id || this.data.before?.component?.id;
        const wireId = this.sceneData?.wireId || null;

        if (feederId) {
          try { dispatch(removeFeeder(feederId)); } catch (e) { console.warn('[FixtureCommand] removeFeeder failed', e); }
        }
        if (compId) {
          try { dispatch(removeComponent(compId)); } catch (e) { console.warn('[FixtureCommand] removeComponent failed', e); }
        }
        if (wireId) {
          try { dispatch(deleteWire(wireId)); } catch (e) { console.warn('[FixtureCommand] deleteWire failed', e); }
        }
        // ✅ Fixture'ları tesisatSlice'tan da sil — yoksa useTesisatRebuild undo sonrası
        // onları sahneye GERİ kuruyor (fixture sembolleri leftover kalıyordu).
        const undoFixtureIds = this.sceneData?.fixtureIds || [];
        undoFixtureIds.forEach((fid) => {
          if (fid) { try { dispatch(deleteTesisat(fid)); } catch (e) { /* ignore */ } }
        });
      } catch (err) {
        console.warn('[FixtureCommand] Redux cleanup during undo failed', err);
      }
    }

    // ✅ Restore callback: re-enter draw mode with vertices and fixture info
    const restoreFn = (typeof this.restoreCallback === 'function')
      ? this.restoreCallback
      : (this.sceneData && typeof this.sceneData.restoreCallback === 'function')
        ? this.sceneData.restoreCallback
        : (this.options && typeof this.options.restoreCallback === 'function')
          ? this.options.restoreCallback
          : null;

    if (typeof restoreFn === 'function') {
      try {
        const verts = this.restoreVertices || this.sceneData?.restoreVertices || this.sceneData?.wireVertices || [];
        const source = this.restoreSource || this.sceneData?.restoreSource || null;
        const fixtureInfos = this.sceneData?.fixtureInfos || []; // ✅ Fixture bilgilerini al
        
        restoreFn(verts, source, fixtureInfos); // ✅ fixtureInfos'u da geçir
      } catch (err) {
        console.error('[FixtureCommand] restoreCallback threw', err);
      }
    } else {
      
    }
  }

  /**
   * JSON'dan FixtureCommand oluştur
   */
  static fromJSON(json) {
    const action = json.type === 'ADD_LIGHT_FIXTURE' ? 'ADD' : 'DELETE';
    const { component, feeder } = json.data.after || json.data.before;

    // Legacy support: 'fixture' key yerine 'component' key kullanılmış olabilir
    const fixtureData = component || (json.data.after || json.data.before).fixture;

    const cmd = new FixtureCommand(action, fixtureData, feeder, json.sceneData || {});
    // ✅ restore metadata
    cmd.restoreVertices = json.restoreVertices || (json.sceneData && json.sceneData.restoreVertices) || [];
    cmd.restoreSource = (json.sceneData && json.sceneData.restoreSource) || null;
    cmd.restoreCommandType = json.restoreCommandType || (json.sceneData && json.sceneData.restoreCommandType) || null;
    return cmd;
  }

  /**
   * ✅ Serialize to JSON with restore metadata
   * @override
   */
  toJSON() {
    const base = super.toJSON();
    return {
      ...base,
      restoreVertices: this.restoreVertices || [],
      restoreSource: this.restoreSource || null,
      restoreCommandType: this.restoreCommandType || null
    };
  }
}

export default FixtureCommand;
